import type { TPostAttachment } from '@onesource/schemas';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_ACCEPT_NO_VIDEO,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_SIZE_MB,
  attachmentToItem,
  detectAttachmentType,
  isAcceptedAttachment,
  itemToAttachment,
  uploadPostFile,
  type PostAttachmentItem,
} from './post-attachment-upload';

let attachmentIdCounter = 0;
const nextAttachmentId = () => {
  attachmentIdCounter += 1;
  return `att-${attachmentIdCounter}`;
};

/**
 * Manages the attachment list for the post composer and the inline edit form:
 * picking + uploading new files (presigned-PUT R2), removing items, and
 * exposing the resolved `attachments` payload. Seed `initial` (existing
 * attachments) to edit a post's media. Pass `allowVideo: false` to hide video
 * from the file picker and drop any video files that slip through (e.g. members
 * who aren't space managers can't attach videos).
 */
export const usePostAttachments = (initial?: TPostAttachment[] | null, options?: { allowVideo?: boolean }) => {
  const allowVideo = options?.allowVideo ?? true;
  const [items, setItems] = useState<PostAttachmentItem[]>(() => (initial ?? []).map(attachmentToItem));
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Revoke object URLs for freshly picked files on unmount.
  useEffect(() => {
    return () => {
      setItems(prev => {
        prev.forEach(item => {
          if (item.preview !== item.url) {
            URL.revokeObjectURL(item.preview);
          }
        });
        return prev;
      });
    };
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }
    setError(null);

    setItems(current => {
      const remainingSlots = MAX_ATTACHMENTS - current.length;
      // oxlint-disable-next-line unicorn/prefer-spread -- FileList spread needs downlevelIteration, not enabled in this package's tsconfig
      const selected = Array.from(files).slice(0, remainingSlots);

      // Accepted types are images, PDFs, and (admins only) videos — drop the rest.
      const permitted = selected.filter(file => isAcceptedAttachment(file, allowVideo));
      const rejected = selected.filter(file => !isAcceptedAttachment(file, allowVideo));
      // A video is only rejected when the user can't upload video (else it's permitted).
      const blockedVideos = rejected.filter(file => file.type.startsWith('video/'));
      const unsupported = rejected.filter(file => !file.type.startsWith('video/'));

      const oversized = permitted.filter(file => file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024);
      const filesToUpload = permitted.filter(file => file.size <= MAX_ATTACHMENT_SIZE_MB * 1024 * 1024);

      const messages: string[] = [];
      if (blockedVideos.length > 0) {
        messages.push('Only admins can upload videos, so video files were skipped.');
      }
      if (unsupported.length > 0) {
        messages.push('Only images, PDFs, and videos are allowed, so other files were skipped.');
      }
      if (oversized.length > 0) {
        messages.push(`Some files exceed the ${MAX_ATTACHMENT_SIZE_MB}MB limit and were skipped.`);
      }
      if (messages.length > 0) {
        setError(messages.join(' '));
      }

      const newItems: PostAttachmentItem[] = filesToUpload.map(file => {
        const id = nextAttachmentId();
        const preview = URL.createObjectURL(file);
        // Upload in the background; patch the item by id when done.
        uploadPostFile(file)
          .then(({ url, mediaId }) => {
            setItems(prev => prev.map(item => (item.id === id ? { ...item, uploading: false, url, mediaId } : item)));
          })
          .catch(() => {
            setItems(prev => prev.filter(item => item.id !== id));
            URL.revokeObjectURL(preview);
          });
        return {
          id,
          preview,
          uploading: true,
          type: detectAttachmentType(file),
          name: file.name,
          mimeType: file.type || undefined,
          size: file.size,
        };
      });

      return [...current, ...newItems];
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeItem = (id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item && item.preview !== item.url) {
        URL.revokeObjectURL(item.preview);
      }
      return prev.filter(i => i.id !== id);
    });
  };

  // Replace all items, revoking object URLs of any in-flight uploads. Pass
  // existing attachments to (re)seed the edit form, or nothing to clear (e.g.
  // after a successful post).
  const reset = (next?: TPostAttachment[] | null) => {
    setItems(prev => {
      prev.forEach(item => {
        if (item.preview !== item.url) {
          URL.revokeObjectURL(item.preview);
        }
      });
      return (next ?? []).map(attachmentToItem);
    });
    setError(null);
  };

  const clear = () => reset();

  const attachments = useMemo<TPostAttachment[]>(
    () => items.map(itemToAttachment).filter((a): a is TPostAttachment => a !== null),
    [items],
  );

  const isUploading = items.some(item => item.uploading);
  const atLimit = items.length >= MAX_ATTACHMENTS;

  const openFilePicker = () => fileInputRef.current?.click();

  return {
    items,
    attachments,
    error,
    setError,
    isUploading,
    atLimit,
    fileInputRef,
    accept: allowVideo ? ATTACHMENT_ACCEPT : ATTACHMENT_ACCEPT_NO_VIDEO,
    handleFileSelect,
    removeItem,
    clear,
    reset,
    openFilePicker,
  };
};
